// NEW_FILE_JUSTIFIED: Main Knowledge Base page for insurance and real-estate orgs (task 11)
import { useState } from 'react';
import { BookOpen, Upload } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useOrgContext } from '@/hooks/useOrgContext';
import { KnowledgeBaseDocumentsTab } from '@/components/knowledgeBase/KnowledgeBaseDocumentsTab';
// These components are created in parallel tasks 12 and 13 — imports resolve once tasks complete
import { KnowledgeBaseTextsTab } from '@/components/knowledgeBase/KnowledgeBaseTextsTab';
import { KnowledgeBaseWebsitesTab } from '@/components/knowledgeBase/KnowledgeBaseWebsitesTab';
import { DocumentUploadDialog } from '@/components/knowledgeBase/DocumentUploadDialog';
// These hooks are created in task 10 — imports resolve once task completes
import { useKnowledgeBase } from '@/hooks/useKnowledgeBase';
import { useKnowledgeBaseUpload } from '@/hooks/useKnowledgeBaseUpload';
import type { IKnowledgeBasePageProps } from '@/@types/knowledgeBase';

export function KnowledgeBasePage({ orgId }: IKnowledgeBasePageProps) {
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const {
    documents,
    isLoading,
    handleDeleteSelected,
    handleToggleShared,
    handleReprocess,
    handleSaveText,
    handleCrawlWebsite,
    handleRefreshWebsite,
  } = useKnowledgeBase(orgId);

  const { uploadFiles } = useKnowledgeBaseUpload();

  const fileDocs = documents.filter(d => d.sourceType === 'file');
  const textDocs = documents.filter(d => d.sourceType === 'text');
  const websiteDocs = documents.filter(d => d.sourceType === 'website');

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Knowledge Base</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading
              ? 'Loading...'
              : `${documents.length} document${documents.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Button onClick={() => setIsUploadOpen(true)}>
          <Upload aria-hidden="true" className="w-4 h-4 mr-2" />
          Upload
        </Button>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none">
        <Tabs defaultValue="documents">
          <div className="border-b border-table-row-border px-6 pt-4 pb-0">
            <TabsList>
              <TabsTrigger value="documents">
                Documents{!isLoading && fileDocs.length > 0 ? ` (${fileDocs.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="texts">
                Text Entries{!isLoading && textDocs.length > 0 ? ` (${textDocs.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="websites">
                Websites{!isLoading && websiteDocs.length > 0 ? ` (${websiteDocs.length})` : ''}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="documents" className="p-6">
            <KnowledgeBaseDocumentsTab
              orgId={orgId}
              documents={fileDocs}
              isLoading={isLoading}
              onUploadClick={() => setIsUploadOpen(true)}
              onDelete={handleDeleteSelected}
              onToggleShared={handleToggleShared}
              onReprocess={handleReprocess}
            />
          </TabsContent>

          <TabsContent value="texts" className="p-6">
            <KnowledgeBaseTextsTab
              orgId={orgId}
              documents={textDocs}
              isLoading={isLoading}
              onSaveText={handleSaveText}
              onDelete={handleDeleteSelected}
              onToggleShared={handleToggleShared}
            />
          </TabsContent>

          <TabsContent value="websites" className="p-6">
            <KnowledgeBaseWebsitesTab
              orgId={orgId}
              documents={websiteDocs}
              isLoading={isLoading}
              onCrawl={handleCrawlWebsite}
              onDelete={handleDeleteSelected}
              onToggleShared={handleToggleShared}
              onRefresh={handleRefreshWebsite}
            />
          </TabsContent>
        </Tabs>
      </div>

      <DocumentUploadDialog
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        onUpload={async input => {
          await uploadFiles(input.files, orgId, input.isSharedWithCompanies);
        }}
      />
    </div>
  );
}

function KnowledgeBasePageWithOrgGuard() {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <BookOpen className="w-12 h-12 text-muted-foreground mb-4" aria-hidden="true" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage the knowledge base.
          </p>
        </div>
      </div>
    );
  }

  if (orgLoading || !currentOrg) return null;

  return <KnowledgeBasePage orgId={currentOrg._id} />;
}

export { KnowledgeBasePageWithOrgGuard };
